export type TicketKey = 'adult' | 'child' | 'student' | 'member' | 'family'

export type TicketType = {
    key: TicketKey
    label: string
    detail: string
    pence: number
    seats: number
}

export const TICKET_TYPES: TicketType[] = [
    { key: 'adult', label: 'Adult', detail: '16 and over', pence: 1400, seats: 1 },
    { key: 'child', label: 'Child', detail: 'Ages 4 to 15', pence: 900, seats: 1 },
    { key: 'student', label: 'Student', detail: 'With a valid student card', pence: 1100, seats: 1 },
    { key: 'member', label: 'Member', detail: 'Show your membership card', pence: 700, seats: 1 },
    { key: 'family', label: 'Family', detail: 'Two adults and two children', pence: 4000, seats: 4 }
]

export const MAX_SEATS_PER_BOOKING = 12

export const formatPrice = (pence: number): string => {
    const pounds = pence / 100

    return `£${pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2)}`
}

export const seatsFor = (tickets: Partial<Record<TicketKey, number>>): number => TICKET_TYPES.reduce((sum, type) => sum + (tickets[type.key] ?? 0) * type.seats, 0)

export const totalFor = (tickets: Partial<Record<TicketKey, number>>): number => TICKET_TYPES.reduce((sum, type) => sum + (tickets[type.key] ?? 0) * type.pence, 0)

export const describeTickets = (tickets: Partial<Record<TicketKey, number>>): string => TICKET_TYPES
    .filter(type => (tickets[type.key] ?? 0) > 0)
    .map(type => `${tickets[type.key]} ${type.label.toLowerCase()}`)
    .join(', ')

export const MEMBERSHIP_TIERS = [
    {
        key: 'observer',
        name: 'Observer',
        pence: 4500,
        tagline: 'For the curious',
        benefits: [
            'Two free planetarium shows every month',
            'Member price on all other show tickets',
            'Booking opens to you seven days early',
            'The Clear skies guide, with members-only notes',
            'Ten per cent off in the café and shop'
        ]
    },
    {
        key: 'astronomer',
        name: 'Astronomer',
        pence: 9500,
        tagline: 'For regular observers',
        featured: true,
        benefits: [
            'Unlimited planetarium shows',
            'Four telescope nights included each year',
            'Members-only observing nights on the roof',
            'Booking opens to you fourteen days early',
            'Bring a guest free to one show a month',
            'Everything in Observer'
        ]
    },
    {
        key: 'patron',
        name: 'Patron',
        pence: 25000,
        tagline: 'For those who keep the lights off',
        benefits: [
            'A private telescope evening for up to four people',
            'Unlimited telescope nights, subject to space',
            'Evenings with the research team, twice a year',
            'Your name on the Patrons’ wall in the dome',
            'Funds our free school visits and outreach',
            'Everything in Astronomer'
        ]
    }
] as const

export type MembershipKey = typeof MEMBERSHIP_TIERS[number]['key']
